import { useEffect, useState } from 'react'
import tw, { styled, css } from 'twin.macro'
import { RecommendedArticles_recommendedArticles_articles } from '@apollo-types/RecommendedArticles'
import PopLink from '@components/PopLink'
import { appendUrlSearchParams } from '@libs/url-utils'
import { PageComponents } from '@libs/eventTracking/article/common'
import { useCleanPostData } from '@features/post/contexts/CleanPostDataContext'

const visibleStyle = css`
  opacity: 1;
  pointer-events: unset;
`

const Container = styled.div<{ isVisible: boolean }>`
  display: flex;
  position: fixed;
  bottom: 68px;
  left: 16px;
  right: 16px;
  box-shadow: 0px 4px 24px rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  overflow: hidden;
  ${tw`bg-white z-mask`};

  opacity: 0;
  pointer-events: none;
  transition: opacity 250ms;
  ${(props) => props.isVisible && visibleStyle};
`

const Img = styled.img`
  display: block;
  width: 100px;
  height: 80px;
  object-fit: cover;
`

const Content = styled.div`
  padding: 8px 12px;
  font-size: 14px;
  line-height: 1.4;
  font-weight: 500;

  & > * + * {
    margin-top: 4px;
  }
`

const NextRecommendText = styled.div`
  ${tw`text-primary-indigo`}
`

const Title = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-weight: 500;
`

type Props = {
  article: RecommendedArticles_recommendedArticles_articles
}

const FixedRecommendedArticleCard = (props: Props) => {
  const { article } = props
  const { url, coverImage, title } = article
  const articleImageUrl = coverImage?.croppedUrl
  const [isVisible, setIsVisible] = useState(false)
  const { url: pageUrl } = useCleanPostData()
  const recommendedUrl = appendUrlSearchParams(url, {
    fromPage: { pageName: 'article', pageUrl, pageComponent: PageComponents.RECOMMENDER_CARD },
  })

  useEffect(() => {
    const children = document.querySelectorAll('.post-content .post .content > p')
    const start = children[Math.floor(children.length * 0.6)]
    const end = document.querySelector('.recommended-articles')

    if (!start || !end) return

    let isVisible = false

    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0]
      const isStart = entry.target === start
      const { y } = entry.boundingClientRect
      const { isIntersecting } = entry

      // 如果看不見的情況
      if (!isVisible) {
        // 無論是 start 、 end 都會進入 isVisible = true
        if ((isStart && isIntersecting) || (!isStart && !isIntersecting && y > 0)) isVisible = true
      } else {
        if ((isStart && !isIntersecting && y > 0) || (!isStart && isIntersecting)) {
          isVisible = false
        }
      }

      setIsVisible(isVisible)
    })

    observer.observe(start)
    observer.observe(end)
    return () => observer.disconnect()
  }, [])

  return (
    <PopLink url={recommendedUrl} shouldOpenInCurrentTab>
      <Container isVisible={isVisible}>
        <Img src={articleImageUrl} />
        <Content>
          <NextRecommendText>看下一篇：</NextRecommendText>
          <Title>{title}</Title>
        </Content>
      </Container>
    </PopLink>
  )
}

export default FixedRecommendedArticleCard
